import { Component, forwardRef, Input, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import * as moment from 'moment-timezone';
import { TimezoneSelectConfig } from 'src/app/models/timezone-select-config.model';
import { TimeZone } from 'src/app/models/user.model';
import { US_TIMEZONES } from './us-timezones';

/**
 * Drop-in replacement for the previous timezone picker library that only offers US timezones.
 *
 * It emits the exact same `TimeZone` object shape the rest of the app already persists
 * and reads (e.g. the date pipe and patient chip use `.nameValue` / `.abbr`), so no data
 * migration or downstream changes are required.
 */
@Component({
  selector: 'app-timezone-picker',
  templateUrl: './timezone-picker.component.html',
  styleUrls: ['./timezone-picker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TimezonePickerComponent),
      multi: true,
    },
  ],
})
export class TimezonePickerComponent implements OnInit, ControlValueAccessor {
  @Input() customPlaceholderText = 'Choose...';
  @Input() customNotFoundText = 'No zone found';
  @Input() clearable = false;
  @Input() virtualScroll = false;

  // Single source of truth for the disabled state. Set either by the `disabled` @Input or
  // by Angular forms via `setDisabledState`, and consumed by the template through the getter.
  private _disabled = false;
  @Input()
  set disabled(isDisabled: boolean) {
    this._disabled = isDisabled;
  }
  get disabled(): boolean {
    return this._disabled;
  }

  @Input()
  set config(conf: TimezoneSelectConfig) {
    if (conf) {
      // Always render the panel on <body> so it is not clipped by the `overflow: hidden` of
      // ancestor dialogs / mat-steppers (regardless of what a caller passes).
      this._config = { ...conf, appendTo: 'body' };
    }
  }
  get config(): TimezoneSelectConfig {
    return this._config;
  }
  private _config: TimezoneSelectConfig = {
    hideSelected: false,
    dropdownPosition: 'auto',
    appearance: 'underline',
    clearOnBackspace: true,
    closeOnSelect: true,
    appendTo: 'body',
  };

  timeZones: TimeZone[] = [];
  selectedZone: TimeZone = null;

  // No-op defaults until the parent form registers its callbacks via registerOnChange / registerOnTouched.
  private propagateChange: (value: TimeZone) => void = () => {
    /* noop */
  };
  private propagateTouched: () => void = () => {
    /* noop */
  };

  ngOnInit(): void {
    // Main US timezones with friendly labels (Eastern, Central, Mountain, Arizona, ...).
    this.timeZones = US_TIMEZONES.map((option) => this.buildZone(option.zone, option.label));
  }

  /**
   * Build the full `TimeZone` object from an IANA zone, deriving abbreviation and UTC
   * offset from moment-timezone so they remain accurate across DST changes. `label` is the
   * user-facing display name; falls back to the IANA id for legacy/out-of-list zones.
   */
  private buildZone(zone: string, label?: string): TimeZone {
    const utc = moment.tz(zone).format('Z');
    return {
      name: label ?? `${zone} (${utc})`,
      nameValue: zone,
      timeValue: utc,
      group: 'United States',
      abbr: moment.tz(zone).zoneAbbr(),
    };
  }

  onChange(zone: TimeZone): void {
    this.selectedZone = zone ?? null;
    this.propagateChange(this.selectedZone);
    this.propagateTouched();
  }

  registerOnChange(fn: (value: TimeZone) => void): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.propagateTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabled = isDisabled;
  }

  writeValue(zone: string | TimeZone): void {
    if (!zone) {
      this.selectedZone = null;
      return;
    }

    const nameValue = typeof zone === 'string' ? zone : zone.nameValue;
    let match = this.timeZones.find((z) => z.nameValue === nameValue);

    // Preserve a previously-saved value that is not in the curated US list (e.g. legacy
    // data) so it still displays instead of silently clearing the field.
    if (!match && nameValue) {
      match = typeof zone === 'object' ? zone : this.buildZone(nameValue);
      this.timeZones = [match, ...this.timeZones];
    }

    this.selectedZone = match ?? null;
  }
}
